import { exhaustiveCheck } from '@openx/utils/lib/exhaustiveCheck.ts';
import { format, isValid, parse, startOfDay } from 'date-fns';
import { t } from 'i18next';
import type { BuildErrorMessageArgs, DateFieldErrorCode, ValidateDatesArgs, ValidateDatesResult } from './types.ts';

export const parseDate = (str: string | undefined, inputFormat: string): Date | null => {
  if (!str) return null;

  const parsed = parse(str, inputFormat, new Date());

  return isValid(parsed) ? startOfDay(parsed) : null;
};

export const normalizeToStartOfDay = (date: Date | null): Date | null => {
  if (!date || !isValid(date)) return null;

  return startOfDay(date);
};

export const formatDate = (date: Date | null, inputFormat: string) => {
  const normalized = normalizeToStartOfDay(date);

  return normalized ? format(normalized, inputFormat) : undefined;
};

export const buildErrorMessage = ({ code, side, minDate, maxDate }: BuildErrorMessageArgs): string => {
  switch (code) {
    case 'INVALID_FORMAT':
      return t('Invalid date format');
    case 'MIN_BEFORE_BOUND':
      return t('Min date must be {minDate}', { minDate });
    case 'MAX_AFTER_BOUND':
      return t('Max date must be {maxDate}', { maxDate });
    case 'INVALID_ORDER':
      return side === 'start'
        ? t('Min has to be less or equal than Max')
        : t('Max has to be greater or equal than Min');
  }

  exhaustiveCheck(code);
};

const buildValidationResult = (
  start: DateFieldErrorCode | undefined,
  end: DateFieldErrorCode | undefined
): ValidateDatesResult => ({ start, end });

export const validateDates = ({
  currentDate,
  otherDate,
  side,
  minDate,
  maxDate,
  inputFormat,
}: ValidateDatesArgs): ValidateDatesResult => {
  const otherNonOrderError = otherDate.error && otherDate.error !== 'INVALID_ORDER' ? otherDate.error : undefined;

  // 1. Empty current side
  if (!currentDate) {
    return side === 'start'
      ? buildValidationResult(undefined, otherNonOrderError)
      : buildValidationResult(otherNonOrderError, undefined);
  }

  // 2. Normalize / invalid format
  const normalizedCurrent = normalizeToStartOfDay(currentDate);

  if (!normalizedCurrent) {
    return side === 'start'
      ? buildValidationResult('INVALID_FORMAT', otherNonOrderError)
      : buildValidationResult(otherNonOrderError, 'INVALID_FORMAT');
  }

  // 3a. Boundary min check
  if (normalizedCurrent < minDate) {
    return side === 'start'
      ? buildValidationResult('MIN_BEFORE_BOUND', otherNonOrderError)
      : buildValidationResult(otherNonOrderError, 'MIN_BEFORE_BOUND');
  }

  // 3b. Boundary max check
  if (normalizedCurrent > maxDate) {
    return side === 'start'
      ? buildValidationResult('MAX_AFTER_BOUND', otherNonOrderError)
      : buildValidationResult(otherNonOrderError, 'MAX_AFTER_BOUND');
  }

  const otherComparable = otherDate.date && !otherDate.error ? parseDate(otherDate.date, inputFormat) : null;

  // 4. Ordering (equality allowed)
  if (
    otherComparable &&
    ((side === 'start' && normalizedCurrent.getTime() > otherComparable.getTime()) ||
      (side === 'end' && normalizedCurrent.getTime() < otherComparable.getTime()))
  ) {
    return { start: 'INVALID_ORDER', end: 'INVALID_ORDER' };
  }

  // 5. Success
  return side === 'start'
    ? buildValidationResult(undefined, otherNonOrderError)
    : buildValidationResult(otherNonOrderError, undefined);
};
